import { Link, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users, Database } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { ResearchEvent } from '../types/events';
import { eventTypeLabel } from '../types/events';
import { DashPanel } from '../components/DashPanel';
import { EntityActionsMenu } from '../components/EntityActionsMenu';
import { useEvents } from '../context/EventsContext';

interface EventCardProps {
  event: ResearchEvent;
}

export function EventCard({ event }: EventCardProps) {
  const { deleteEvent } = useEvents();
  const navigate = useNavigate();
  const periodLabel =
    event.noPeriod || !event.startDate || !event.endDate
      ? 'Todos os dados do Firebase'
      : `${format(parseISO(event.startDate), 'dd/MM/yy', { locale: ptBR })} — ${format(parseISO(event.endDate), 'dd/MM/yy', { locale: ptBR })}`;

  return (
    <Link to={`/dashboard/events/${event.id}`} className="block group relative">
      <EntityActionsMenu
        className="absolute top-2 right-2 z-10"
        onEdit={() => navigate(`/dashboard/events/${event.id}/edit`)}
        onDelete={() => deleteEvent(event.id)}
        deleteTitle="Excluir evento"
        deleteDescription={`Tem certeza que deseja excluir "${event.title}"? Essa ação não pode ser desfeita.`}
      />
      <DashPanel padding="none" className="overflow-hidden hover:border-primary/40 transition-colors h-full">
        <div className="aspect-[16/9] bg-muted overflow-hidden">
          {event.photoUrl ? (
            <img
              src={event.photoUrl}
              alt=""
              className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
              Sem foto
            </div>
          )}
        </div>
        <div className="p-4 space-y-3">
          <div>
            <span className="text-[10px] font-medium uppercase tracking-wide text-primary bg-primary/10 px-2 py-0.5 rounded-full">
              {eventTypeLabel(event)}
            </span>
            <h3 className="text-sm font-semibold text-foreground mt-2 line-clamp-2">
              {event.title}
            </h3>
          </div>
          <div className="space-y-1.5 text-xs text-muted-foreground">
            <p className="flex items-center gap-1.5">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{event.school}</span>
            </p>
            <p className="flex items-center gap-1.5">
              <Calendar className="h-3 w-3 shrink-0" />
              {periodLabel}
            </p>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-border text-xs">
            <span className="flex items-center gap-1 text-muted-foreground">
              <Users className="h-3 w-3" />
              {event.participantCount}
            </span>
            <span className="flex items-center gap-1 text-muted-foreground">
              <Database className="h-3 w-3" />
              {event.recordCount.toLocaleString('pt-BR')}
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground truncate">
            {event.createdBy}
          </p>
        </div>
      </DashPanel>
    </Link>
  );
}
